import { instantToFloating } from "../zone.js";

/**
 * Low-level RFC 5545 text + date helpers. Pure string work only.
 */

/** Escape a TEXT value: backslash, semicolon, comma, newline. */
export function escapeText(s: string): string {
  return s
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r\n|\n|\r/g, "\\n");
}

/** Inverse of {@link escapeText}. */
export function unescapeText(s: string): string {
  return s.replace(/\\([\\;,nN])/g, (_, c: string) => (c === "n" || c === "N" ? "\n" : c));
}

/**
 * Fold a content line to <=75 octets per RFC 5545 §3.1, continuation lines
 * prefixed with a single space. We fold on a 74-char boundary (ASCII-safe; a
 * stray multi-byte split is harmless for our own round-trip and tolerated by
 * importers).
 */
export function foldLine(line: string): string {
  if (line.length <= 75) return line;
  const out: string[] = [];
  let i = 0;
  out.push(line.slice(0, 75));
  i = 75;
  while (i < line.length) {
    out.push(" " + line.slice(i, i + 74));
    i += 74;
  }
  return out.join("\r\n");
}

/** Unfold: join continuation lines (CRLF/LF followed by space or tab). */
export function unfold(text: string): string[] {
  const raw = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
  const lines: string[] = [];
  for (const line of raw) {
    if ((line.startsWith(" ") || line.startsWith("\t")) && lines.length) {
      lines[lines.length - 1] += line.slice(1);
    } else {
      lines.push(line);
    }
  }
  return lines.filter((l) => l.length > 0);
}

function pad(n: number, w = 2): string {
  return String(n).padStart(w, "0");
}

/** ISO instant → "YYYYMMDDTHHMMSSZ" (UTC form). */
export function formatUtc(iso: string): string {
  const d = new Date(iso);
  return (
    `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}` +
    `T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`
  );
}

/** Merge a rule's canonical `until` column into its RRULE string (stripping any
 *  embedded UNTIL first). UNTIL is emitted as a UTC instant. Shared by the ICS
 *  exporter and the Google Calendar mapper, which build identical RRULE strings. */
export function rruleWithUntil(rrule: string, until: string | null): string {
  const parts = rrule.split(";").filter((p) => !/^UNTIL=/i.test(p));
  if (until) parts.push(`UNTIL=${formatUtc(until)}`);
  return parts.join(";");
}

/** A floating Date's UTC fields → "YYYYMMDDTHHMMSS" (no trailing Z). */
export function formatFloating(floating: Date): string {
  return (
    `${floating.getUTCFullYear()}${pad(floating.getUTCMonth() + 1)}${pad(floating.getUTCDate())}` +
    `T${pad(floating.getUTCHours())}${pad(floating.getUTCMinutes())}${pad(floating.getUTCSeconds())}`
  );
}

/** ISO instant → "YYYYMMDD" date in `tz` (or UTC when tz null). */
export function formatDate(iso: string, tz: string | null): string {
  const d = tz ? instantToFloating(iso, tz) : new Date(iso);
  return `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}`;
}

/**
 * Render a DTSTART/DTEND/EXDATE-style value for an instant.
 * Returns the property param suffix (e.g. ";TZID=...;VALUE=DATE") plus value.
 */
export function formatDateProp(iso: string, tz: string | null, allDay: boolean): { params: string; value: string } {
  if (allDay) return { params: ";VALUE=DATE", value: formatDate(iso, tz) };
  if (tz) return { params: `;TZID=${tz}`, value: formatFloating(instantToFloating(iso, tz)) };
  return { params: "", value: formatUtc(iso) };
}
